import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Mail, Phone, MapPin, ArrowUpRight, Loader2 } from 'lucide-react';

const FinalCTA = () => {
  const [formData, setFormData] = useState({ name: '', company: '', email: '', message: '' });
  const [status, setStatus] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const resp = await fetch(`${API_URL}/api/v1/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await resp.json().catch(() => ({}));
      
      if (resp.ok || data?.success) {
        setStatus('success');
        setFormData({ name: '', company: '', email: '', message: '' });
      } else {
        setStatus('error');
      }
    } catch (err) {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="py-24 bg-[#030712] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="bento-card p-8 sm:p-12 lg:p-16 bg-[#0B1120] border border-white/10"
        >
          <div className="grid lg:grid-cols-12 gap-12 items-start">

            {/* Left Column: Direct Consultation Copy */}
            <div className="lg:col-span-6 flex flex-col justify-between h-full">
              <div>
                <div className="section-label-dark mb-3">Start Building</div>
                <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-tight mb-6">
                  Ready to engineer your <span className="gradient-text">next software system?</span>
                </h2>
                <p className="text-slate-300 text-base leading-relaxed mb-8 max-w-lg font-normal">
                  Tell us about your project requirements or manual friction points — our engineering team directed by CTO Haris will review your scope and respond within 24 hours.
                </p>

                {/* Direct Contact Details Grid */}
                <div className="space-y-4 mb-8">
                  <a
                    href="mailto:haris@harisbuilds.com"
                    className="flex items-center gap-3 text-slate-300 hover:text-sky-400 transition-colors text-sm font-medium w-fit group"
                  >
                    <div className="w-9 h-9 rounded-xl bg-slate-900 border border-white/10 flex items-center justify-center text-sky-400 group-hover:border-sky-400/40">
                      <Mail size={18} />
                    </div>
                    <span>haris@harisbuilds.com</span>
                  </a>

                  <a
                    href="https://wa.me/12395177689"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-slate-300 hover:text-emerald-400 transition-colors text-sm font-medium w-fit group"
                  >
                    <div className="w-9 h-9 rounded-xl bg-slate-900 border border-white/10 flex items-center justify-center text-emerald-400 group-hover:border-emerald-400/40">
                      <Phone size={18} />
                    </div>
                    <span>+1 239 517 7689 (WhatsApp Direct)</span>
                  </a>

                  <div className="flex items-center gap-3 text-slate-400 text-sm font-medium">
                    <div className="w-9 h-9 rounded-xl bg-slate-900 border border-white/10 flex items-center justify-center text-purple-400">
                      <MapPin size={18} />
                    </div>
                    <span>Islamabad HQ &amp; Global Delivery</span>
                  </div>
                </div>
              </div>

              {/* Guarantees */}
              <div className="pt-6 border-t border-white/10 space-y-2">
                {[
                  'Guaranteed reply within 24 business hours',
                  'Fixed milestone pricing with zero surprises',
                  '100% confidential NDA upon request',
                ].map((item) => (
                  <div key={item} className="flex items-center gap-2 text-xs text-slate-400">
                    <CheckCircle2 size={14} className="text-emerald-400 flex-shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Column: High-converting Consultation Form */}
            <div className="lg:col-span-6 bg-slate-950/80 p-6 sm:p-8 rounded-2xl border border-white/10 shadow-2xl">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-xs font-mono font-semibold uppercase tracking-wider text-slate-400 mb-2">
                    Your Full Name
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="form-input"
                    placeholder="e.g. Sarah Jenkins"
                    id="contact-name"
                    autoComplete="name"
                  />
                </div>

                <div>
                  <label className="block text-xs font-mono font-semibold uppercase tracking-wider text-slate-400 mb-2">
                    Company / Project Name
                  </label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="form-input"
                    placeholder="e.g. Acme Corp or My Startup"
                    id="contact-company"
                    autoComplete="organization"
                  />
                </div>

                <div>
                  <label className="block text-xs font-mono font-semibold uppercase tracking-wider text-slate-400 mb-2">
                    Work Email Address
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="form-input"
                    placeholder="e.g. sarah@company.com"
                  />
                </div>

                <div>
                  <label className="block text-xs font-mono font-semibold uppercase tracking-wider text-slate-400 mb-2">
                    Project Requirements or Automation Needs
                  </label>
                  <textarea
                    rows={4}
                    required
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="form-input resize-y min-h-[120px]"
                    placeholder="Tell us what custom software, AI agent, or workflow automation you want to build..."
                  />
                </div>

                {/* Solid Action Button */}
                <button
                  type="submit"
                  disabled={status === 'sending'}
                  className="btn-primary w-full py-4 text-base font-bold justify-center"
                >
                  {status === 'sending' ? (
                    <>
                      <Loader2 size={18} className="animate-spin text-[#030712]" />
                      <span>Sending Request...</span>
                    </>
                  ) : (
                    <>
                      <span>Submit Strategy Request</span>
                      <ArrowUpRight size={18} strokeWidth={2.5} />
                    </>
                  )}
                </button>

                <p className="text-[11px] font-mono text-center text-slate-500">
                  No commitment required. Initial consultation is 100% free.
                </p>

                {/* Status Feedback Alerts */}
                {status === 'success' && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-xs font-semibold text-center flex items-center justify-center gap-2"
                  >
                    <CheckCircle2 size={16} />
                    <span>Message received — Haris &amp; team will reach out within 24 hours.</span>
                  </motion.div>
                )}

                {status === 'error' && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-400 text-xs font-semibold text-center"
                  >
                    Something went wrong — please email haris@harisbuilds.com directly.
                  </motion.div>
                )}
              </form>
            </div>

          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default FinalCTA;
